import type { RecursivePartial } from "@intelligems/ig-types/utils";

type MergeValue = (original: unknown, value: unknown, path: string) => unknown;

interface Merger<T> {
  merge(
    partial: RecursivePartial<T> | undefined,
    mergeValue?: MergeValue
  ): void;
}

// none of our merge deep utility has the exact behavior we need
// hence the custom implementation
export function merge<T extends object>(
  base: T,
  partial: RecursivePartial<T> | ((merger: Merger<T>) => void) | undefined
) {
  const merged = JSON.parse(JSON.stringify(base)) as T;

  const merger: Merger<T> = {
    merge(partial, mergeValue) {
      if (isMergeable(partial)) {
        const clone = JSON.parse(
          JSON.stringify(partial)
        ) as RecursivePartial<T>;
        mergeObjects(merged, clone, "", mergeValue);
      }
    },
  };
  if (typeof partial === "function") {
    partial(merger);
  } else if (partial) {
    merger.merge(partial);
  }

  return merged;
}

function mergeObjects<T extends object, S extends object>(
  a: T,
  b: S,
  path: string,
  mergeValue?: MergeValue
) {
  for (const [key, value] of Object.entries(b)) {
    if (value !== undefined) {
      const newPath = path + "." + key;
      if (isMergeable(a[key]) && isMergeable(value)) {
        mergeObjects(a[key], value, newPath, mergeValue);
      } else if (Array.isArray(value)) {
        const arr = JSON.parse(JSON.stringify(value)) as unknown;
        if (mergeValue === undefined) {
          a[key] = arr;
        } else {
          a[key] = mergeValue(a[key], arr, newPath);
        }
      } else {
        if (mergeValue === undefined) {
          a[key] = value as unknown;
        } else {
          a[key] = mergeValue(a[key], value, newPath);
        }
      }
    }
  }
}

function isMergeable(value: unknown): value is object {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}
